import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { Tag } from '@/components/Tag';
import { LuRefreshCw } from 'react-icons/lu';
import type { SharedJobPostList } from './SharedJobPostListFilterDialog';

interface Props {
  list: SharedJobPostList;
  isSyncingOnServer: boolean;
  lastSyncedAt: Date | null;
  formatDate: (date: Date) => string;
  onSelectList: (listId: number) => void;
  onSyncList: (e: React.MouseEvent, listId: number) => void;
}

export default function SharedJobPostListRow({
  list,
  isSyncingOnServer,
  lastSyncedAt,
  formatDate,
  onSelectList,
  onSyncList,
}: Props) {
  const [showMeta, setShowMeta] = useState(false);

  const isListSyncing = list.sync_status === 'syncing';
  const isListSyncDisabled = isListSyncing || isSyncingOnServer;

  let lastImportedAtLabel: string;
  if (list.last_imported_at) {
    lastImportedAtLabel = `最終同期日：${formatDate(new Date(list.last_imported_at))}`;
  } else if (lastSyncedAt) {
    lastImportedAtLabel = `最終同期日：${formatDate(lastSyncedAt)}`;
  } else {
    lastImportedAtLabel = 'まだ同期されていません';
  }

  return (
    <div
      className="flex w-full cursor-pointer items-center justify-between border-x-0 border-t-0 border-b border-solid border-gray-100 bg-transparent p-3 hover:bg-gray-50"
      onClick={() => onSelectList(list.id)}
    >
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm">{list.name}</span>
        {list.owner_login_failed && (
          <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
            <Tag
              color="red"
              size="sm"
              title="このリストを同期するアカウントのログインに失敗しているため、最新の求人が取得できません"
            >
              ログイン失敗
            </Tag>
            {list.owner_login_id && (
              <span className="text-xs text-sub">ID: {list.owner_login_id}</span>
            )}
            <Link
              to="/agent/linked_accounts/shared"
              className="text-xs text-primary no-underline hover:underline"
            >
              再ログイン
            </Link>
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {showMeta && <span className="text-xs text-sub">{lastImportedAtLabel}</span>}
        <Button
          color="tertiary"
          size="sm"
          type="button"
          disabled={isListSyncDisabled}
          onClick={(e) => onSyncList(e, list.id)}
          title={lastImportedAtLabel}
          onMouseEnter={() => setShowMeta(true)}
          onMouseLeave={() => setShowMeta(false)}
          onFocus={() => setShowMeta(true)}
          onBlur={() => setShowMeta(false)}
        >
          <LuRefreshCw className={`size-4 ${isListSyncing ? 'animate-spin' : ''}`} />
          <span className="ml-1 text-xs">同期</span>
        </Button>
      </div>
    </div>
  );
}
